import { useQuery } from '@tanstack/react-query'
import { Activity, CheckCircle, AlertTriangle, XCircle, CircleMinus } from 'lucide-react'
import { systemApi } from '../api/client'
import { useExpandablePanel } from '../hooks/useExpandablePanel'
import { ExpandToggle } from './ExpandToggle'
import './AgentHealthGrid.css'

interface AgentHealth {
  name: string
  status: 'healthy' | 'degraded' | 'error' | 'not_configured'
  lastHeartbeat: string | null
  tasksProcessed: number
  avgLatency: number
  currentTask: string | null
}

const statusConfig = {
  healthy: { Icon: CheckCircle, className: 'healthy' },
  degraded: { Icon: AlertTriangle, className: 'degraded' },
  error: { Icon: XCircle, className: 'error' },
  not_configured: { Icon: CircleMinus, className: 'not-configured' },
}

function AgentCard({ agent }: { agent: AgentHealth }) {
  const { Icon, className } = statusConfig[agent.status] || statusConfig.error

  const timeSinceHeartbeat = () => {
    // null/empty lastHeartbeat: agent registered but has never reported a
    // heartbeat yet (e.g., it's idle-since-startup, or the heartbeat path
    // wasn't wired). Render "—" instead of computing Date.now() - 0 which
    // would render as "~494292h ago" (epoch math).
    if (!agent.lastHeartbeat) return '—'
    const diff = Date.now() - new Date(agent.lastHeartbeat).getTime()
    const seconds = Math.floor(diff / 1000)
    if (seconds < 60) return `${seconds}s ago`
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    return `${Math.floor(minutes / 60)}h ago`
  }

  return (
    <div className={`agent-card ${className}`}>
      <div className="agent-header">
        <h4 className="agent-name">{agent.name}</h4>
        <Icon size={18} className="status-icon" />
      </div>

      <div className="agent-stats">
        <div className="stat">
          <span className="stat-label">Latency</span>
          <span className="stat-value mono">{agent.avgLatency}ms</span>
        </div>
        <div className="stat">
          <span className="stat-label">Tasks</span>
          <span className="stat-value mono">{agent.tasksProcessed}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Last seen</span>
          <span className="stat-value">{timeSinceHeartbeat()}</span>
        </div>
      </div>

      {agent.currentTask && (
        <div className="agent-task">
          <Activity size={12} className="task-icon" />
          <span className="task-text">{agent.currentTask}</span>
        </div>
      )}
    </div>
  )
}

export function AgentHealthGrid() {
  const { expanded, toggle, scrollClassName } = useExpandablePanel('agent-grid')
  const { data: agents, isLoading } = useQuery({
    queryKey: ['agent-health'],
    queryFn: systemApi.getAgents,
    refetchInterval: 5000,
  })

  const healthyCount = agents?.filter((a) => a.status === 'healthy').length ?? 0
  const totalCount = agents?.length ?? 0

  return (
    <div className="card agent-health-card">
      <div className="card-header">
        <h3 className="card-title">
          <Activity size={20} className="icon" />
          Agent Coordinator
        </h3>
        <div className="card-header-right">
          {totalCount > 0 && (
            <span className={`card-badge ${healthyCount === totalCount ? 'success' : 'warning'}`}>
              {healthyCount}/{totalCount} healthy
            </span>
          )}
          <ExpandToggle expanded={expanded} onToggle={toggle} />
        </div>
      </div>

      {isLoading ? (
        <div className="loading">
          <div className="spinner" />
        </div>
      ) : (
        <div className={scrollClassName}>
          <div className="agent-grid">
            {agents?.map((agent) => (
              <AgentCard key={agent.name} agent={agent as AgentHealth} />
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
